import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';

import { MatChipsModule } from '@angular/material/chips';
import { GroupsService } from 'src/app/views/groups/groups.service';

@Component({
  selector: 'app-user-create',
  templateUrl: './user-create.component.html',
  styleUrls: ['./user-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    TranslateModule,
    SelectComponent,
    MatChipsModule,
  ],
})
export class UserCreateComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private auth = inject(AuthService);
  private groupsService = inject(GroupsService);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private router = inject(Router);
  dialogRef = inject<MatDialogRef<UserCreateComponent>>(MatDialogRef, { optional: true })!;
  private injectedDialogData = inject(MAT_DIALOG_DATA, { optional: true })!;

  get dialogData() {
    return this.injectedDialogData;
  }

  userForm;
  inviteMode = signal<'single' | 'bulk'>('single');
  bulkRawInput = signal<string>('');
  csvErrors = signal<{ line: number; message: string }[]>([]);
  csvValidated = signal<boolean>(false);
  groupOptions = signal<SelectOption[]>([]);

  parsedEmails = computed(() => {
    const raw = this.bulkRawInput();
    if (!raw) return [];
    const tokens = raw.split(/[\s,;\n]+/);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid = tokens.map((t) => t.trim().toLowerCase()).filter((t) => emailRegex.test(t));
    return [...new Set(valid)];
  });

  roleOptions: SelectOption[] = [
    { value: 'reader', label: 'Roles.Reader' },
    { value: 'writer', label: 'Roles.Writer' },
    { value: 'admin', label: 'Roles.Admin' },
  ];

  ngOnInit(): void {
    this.buildForm();
    this.loadGroups();
  }

  loadGroups() {
    this.groupsService.getGroups().subscribe({
      next: (groups: any[]) => {
        const opts: SelectOption[] = [{ value: null, label: 'UserManagement.DefaultGeneralGroup' }, ...(groups || []).map((g) => ({ value: g._id, label: g.name }))];
        this.groupOptions.set(opts);
      },
      error: () => {
        this.groupOptions.set([{ value: null, label: 'UserManagement.DefaultGeneralGroup' }]);
      },
    });
  }

  buildForm() {
    const data = this.dialogData;
    let formConfig: Record<string, unknown[]> = {};

    if (data) {
      formConfig = {
        firstName: [data.firstName || data.fullName || '', [Validators.required]],
        lastName: [data.lastName || '', [Validators.required]],
        email: [data.email || '', [Validators.required, Validators.email]],
      };
    } else {
      formConfig = {
        email: ['', [Validators.email]],
        role: ['reader'],
        groupId: [null],
      };
    }

    this.userForm = this.fb.group(formConfig);
  }

  downloadTemplate() {
    const content = 'email\nlearner1@example.com\nlearner2@example.com\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'skillztank_invite_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (text) {
          this.parseAndValidateCsv(text);
          this.inviteMode.set('bulk');
        }
      };
      reader.readAsText(file);
    }
  }

  parseAndValidateCsv(text: string) {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) {
      this.csvErrors.set([{ line: 1, message: this.translate.instant('UserManagement.CsvEmpty') }]);
      this.csvValidated.set(true);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const headerLine = lines[0].toLowerCase();
    const headers = headerLine.split(/[,;\t]+/).map((h) => h.trim().replace(/^["']|["']$/g, ''));
    const emailIndex = headers.findIndex((h) => ['email', 'e-mail', 'adresse email', 'adresse_email'].includes(h));

    const errors: { line: number; message: string }[] = [];
    const validEmails: string[] = [];

    if (emailIndex === -1) {
      // Check if line 1 itself is data (no header)
      if (emailRegex.test(headers[0])) {
        for (let i = 0; i < lines.length; i++) {
          const row = lines[i].split(/[,;\t]+/).map((c) => c.trim().replace(/^["']|["']$/g, ''));
          const targetEmail = row[0]?.toLowerCase();
          if (!targetEmail) {
            errors.push({ line: i + 1, message: this.translate.instant('UserManagement.CsvRowEmpty') });
          } else if (!emailRegex.test(targetEmail)) {
            errors.push({ line: i + 1, message: `${this.translate.instant('UserManagement.CsvInvalidEmail')}: "${targetEmail}"` });
          } else {
            validEmails.push(targetEmail);
          }
        }
      } else {
        errors.push({ line: 1, message: this.translate.instant('UserManagement.CsvMissingEmailHeader') });
      }
    } else {
      // Has header row
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i].split(/[,;\t]+/).map((c) => c.trim().replace(/^["']|["']$/g, ''));
        const targetEmail = row[emailIndex]?.toLowerCase();
        if (!targetEmail) {
          errors.push({ line: i + 1, message: this.translate.instant('UserManagement.CsvRowEmpty') });
        } else if (!emailRegex.test(targetEmail)) {
          errors.push({ line: i + 1, message: `${this.translate.instant('UserManagement.CsvInvalidEmail')}: "${targetEmail}"` });
        } else {
          validEmails.push(targetEmail);
        }
      }
    }

    const uniqueValid = [...new Set(validEmails)];
    this.bulkRawInput.set(uniqueValid.join('\n'));
    this.csvErrors.set(errors);
    this.csvValidated.set(true);
  }

  editUser(formValue: Record<string, string>) {
    const payload = { ...formValue, _id: this.dialogData._id };
    this.auth.updateAccount(payload).subscribe({
      next: () => {
        this.dialogRef?.close(true);
      },
      error: () => {
        this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
      },
    });
  }

  inviteUser(formValue: { email: string; role: string; groupId?: string | null }) {
    let payload: { email?: string; emails?: string[]; role: string; groupId?: string | null };

    if (this.inviteMode() === 'bulk') {
      const emails = this.parsedEmails();
      if (emails.length === 0) {
        this.snackbar.open(this.translate.instant('UserManagement.NoValidEmails'), 'Ok', { duration: 2000 });
        return;
      }
      payload = { emails, role: formValue.role, groupId: formValue.groupId || null };
    } else {
      if (!formValue.email) {
        this.snackbar.open(this.translate.instant('UserManagement.EmailRequired'), 'Ok', { duration: 2000 });
        return;
      }
      payload = { email: formValue.email, role: formValue.role, groupId: formValue.groupId || null };
    }

    this.auth.inviteUser(payload).subscribe({
      next: (res) => {
        if (res.success) {
          const sent = res.sentCount || 1;
          const msg = sent > 1 ? `${sent} ${this.translate.instant('UserManagement.InvitationsSentCount')}` : this.translate.instant('Snackbar.InvitationSent');
          this.snackbar.open(msg, 'Ok', { duration: 3000 });
          this.dialogRef?.close(true);
        } else {
          switch (res.msg) {
            case 'already-in-workspace':
              this.snackbar.open(this.translate.instant('Snackbar.UserAlreadyInWorkspace'), 'Ok', { duration: 2000 });
              break;
            default:
              this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
              break;
          }
        }
      },
      error: (err) => {
        if (err.error?.code === 'CannotAddMoreUsersOnTrial') {
          const ref = this.snackbar.open(this.translate.instant('Snackbar.CannotAddMoreUsersOnTrial'), this.translate.instant('Billing.Action.UpgradeNow'), { duration: 8000 });
          ref.onAction().subscribe(() => {
            this.dialogRef?.close();
            this.router.navigate(['/myspace/billing-usage']);
          });
        } else {
          this.snackbar.open(this.translate.instant('Snackbar.ErrorOccured'), 'Ok', { duration: 2000 });
        }
      },
    });
  }
}
